import React, { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { InputNumber } from "primereact/inputnumber";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { Checkbox } from "primereact/checkbox";
import authApi from "../../../../service/IdApi";
import { useAppContext } from "../../../../context/appContext";

const EVALUATOR_NONE = "__none__";

const emptyForm = {
    name: "",
    title: "",
    description: "",
    product: null,
    value: 0,
    priority: 1,
    effort: 1,
    evaluatorKey: EVALUATOR_NONE,
    prerequisites: [],
    emailable: false,
    enabled: true,
    primaryActionLabel: "",
    primaryActionPath: "",
    primaryActionAuthRequired: true,
    secondaryActionLabel: "",
    secondaryActionPath: "",
    secondaryActionAuthRequired: true,
};

const RecommendationCatalogDialog = ({ visible, onHide, mode, recommendation, options, onSaved }) => {
    const { displayAlert } = useAppContext();
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);

    const isEdit = mode === "edit";

    useEffect(() => {
        if (!visible) return;
        if (isEdit && recommendation) {
            setForm({
                name: recommendation.name || "",
                title: recommendation.title || "",
                description: recommendation.description || "",
                product: recommendation.product || null,
                value: recommendation.value ?? 0,
                priority: recommendation.priority ?? 1,
                effort: recommendation.effort ?? 1,
                evaluatorKey: recommendation.evaluatorKey || EVALUATOR_NONE,
                prerequisites: recommendation.prerequisites || [],
                emailable: recommendation.emailable === true,
                enabled: recommendation.enabled !== false,
                primaryActionLabel: recommendation.primaryActionLabel || "",
                primaryActionPath: recommendation.primaryActionPath || "",
                primaryActionAuthRequired: recommendation.primaryActionAuthRequired !== false,
                secondaryActionLabel: recommendation.secondaryActionLabel || "",
                secondaryActionPath: recommendation.secondaryActionPath || "",
                secondaryActionAuthRequired: recommendation.secondaryActionAuthRequired !== false,
            });
        } else {
            setForm(emptyForm);
        }
    }, [visible, isEdit, recommendation]);

    const setField = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const productOptions = (options?.products || []).map((p) => ({
        label: p.friendlyName || p.code,
        value: p.code,
    }));
    const priorityOptions = [
        { label: "1 - Normal", value: 1 },
        { label: "2 - Boosted", value: 2 },
        { label: "3 - Urgent", value: 3 },
    ];
    const effortOptions = [
        { label: "1 - Easy", value: 1 },
        { label: "2 - Some effort", value: 2 },
        { label: "3 - High effort", value: 3 },
    ];
    const evaluatorOptions = [
        { label: "None (manual only)", value: EVALUATOR_NONE },
        ...(options?.evaluatorKeys || []).map((k) => ({ label: k, value: k })),
    ];
    const prerequisiteOptions = (options?.names || [])
        .filter((n) => n !== form.name)
        .map((n) => ({ label: n, value: n }));

    const save = async () => {
        if (!isEdit && (!form.name || form.name.trim() === "")) {
            displayAlert("error", "Name is required", "Please enter a unique name.");
            return;
        }
        if (!form.title || form.title.trim() === "") {
            displayAlert("error", "Title is required", "Please enter a title.");
            return;
        }
        if (!form.product) {
            displayAlert("error", "Product is required", "Please select a product.");
            return;
        }

        const body = {
            name: form.name,
            title: form.title,
            description: form.description,
            product: form.product,
            value: form.value,
            priority: form.priority,
            effort: form.effort,
            evaluatorKey: form.evaluatorKey === EVALUATOR_NONE ? null : form.evaluatorKey,
            prerequisites: form.prerequisites,
            emailable: form.emailable === true,
            enabled: form.enabled === true,
            primaryActionLabel: form.primaryActionLabel,
            primaryActionPath: form.primaryActionPath,
            primaryActionAuthRequired: form.primaryActionAuthRequired === true,
            secondaryActionLabel: form.secondaryActionLabel,
            secondaryActionPath: form.secondaryActionPath,
            secondaryActionAuthRequired: form.secondaryActionAuthRequired === true,
        };

        setSaving(true);
        try {
            if (isEdit) {
                await authApi.patch("admin/recommendations/catalog/" + encodeURIComponent(form.name), body);
                displayAlert("success", "Recommendation Updated", `${form.name} was updated.`);
            } else {
                await authApi.post("admin/recommendations/catalog", body);
                displayAlert("success", "Recommendation Added", `${form.name} was added.`);
            }
            if (onSaved) {
                await onSaved();
            }
            onHide();
        } catch (err) {
            const message = err?.response?.data?.message || "Please try again later.";
            displayAlert("error", "Unable to Save", message);
            console.error(err);
        } finally {
            setSaving(false);
        }
    };

    const footer = (
        <div>
            <Button label="Cancel" icon="pi pi-times" className="p-button-text p-button-danger" onClick={onHide} disabled={saving} />
            <Button label="Save" icon="pi pi-check" onClick={save} loading={saving} autoFocus />
        </div>
    );

    if (!visible) return null;

    return (
        <Dialog
            visible={visible}
            style={{ width: "50vw" }}
            breakpoints={{ "1280px": "75vw", "641px": "95vw" }}
            header={isEdit ? "Edit Recommendation" : "Add Recommendation"}
            modal
            footer={footer}
            onHide={onHide}
            className="p-fluid"
        >
            <div className="field">
                <label htmlFor="name">Name</label>
                <InputText
                    id="name"
                    value={form.name}
                    onChange={(e) => setField("name", e.target.value)}
                    disabled={isEdit}
                    placeholder="e.g. add_business_hours"
                />
                {isEdit && <small className="text-color-secondary">The name is the stable key and cannot be changed.</small>}
            </div>
            <div className="field">
                <label htmlFor="title">Title</label>
                <InputText id="title" value={form.title} onChange={(e) => setField("title", e.target.value)} />
            </div>
            <div className="field">
                <label htmlFor="description">Description</label>
                <InputTextarea id="description" rows={3} value={form.description} onChange={(e) => setField("description", e.target.value)} />
            </div>
            <div className="formgrid grid">
                <div className="field col-12 md:col-6">
                    <label htmlFor="product">Product</label>
                    <Dropdown id="product" value={form.product} options={productOptions} onChange={(e) => setField("product", e.value)} placeholder="Select a product" />
                </div>
                <div className="field col-12 md:col-6">
                    <label htmlFor="value">Value</label>
                    <InputNumber id="value" value={form.value} onValueChange={(e) => setField("value", e.value ?? 0)} min={0} max={100} showButtons />
                </div>
            </div>
            <div className="formgrid grid">
                <div className="field col-12 md:col-6">
                    <label htmlFor="priority">Priority</label>
                    <Dropdown id="priority" value={form.priority} options={priorityOptions} onChange={(e) => setField("priority", e.value)} />
                </div>
                <div className="field col-12 md:col-6">
                    <label htmlFor="effort">Effort</label>
                    <Dropdown id="effort" value={form.effort} options={effortOptions} onChange={(e) => setField("effort", e.value)} />
                </div>
            </div>
            <div className="field">
                <label htmlFor="evaluatorKey">Evaluator (auto-completion)</label>
                <Dropdown id="evaluatorKey" value={form.evaluatorKey} options={evaluatorOptions} onChange={(e) => setField("evaluatorKey", e.value)} />
            </div>
            <div className="field">
                <label htmlFor="prerequisites">Prerequisites</label>
                <MultiSelect
                    id="prerequisites"
                    value={form.prerequisites}
                    options={prerequisiteOptions}
                    onChange={(e) => setField("prerequisites", e.value)}
                    placeholder="Select prerequisites"
                    display="chip"
                    filter
                />
            </div>
            <div className="field-checkbox">
                <Checkbox
                    inputId="enabled"
                    checked={form.enabled === true}
                    onChange={(e) => {
                        const checked = e.checked === true;
                        setForm((prev) => ({
                            ...prev,
                            enabled: checked,
                            emailable: checked ? prev.emailable : false,
                        }));
                    }}
                />
                <label htmlFor="enabled" className="ml-2">
                    Enabled (visible in provider UI)
                </label>
            </div>
            <div className="field-checkbox">
                <Checkbox
                    inputId="emailable"
                    checked={form.emailable === true}
                    onChange={(e) => {
                        if (form.enabled !== true) {
                            return;
                        }
                        setField("emailable", e.checked);
                    }}
                    disabled={form.enabled !== true}
                />
                <label htmlFor="emailable" className="ml-2">
                    Include in batch recommendation emails
                </label>
            </div>
            {form.emailable === true && (
                <>
                    <div className="field">
                        <label htmlFor="primaryActionLabel">Primary action button label</label>
                        <InputText
                            id="primaryActionLabel"
                            value={form.primaryActionLabel}
                            onChange={(e) => setField("primaryActionLabel", e.target.value)}
                            placeholder="e.g. Publish Profile"
                        />
                    </div>
                    <div className="field">
                        <label htmlFor="primaryActionPath">Primary action path template</label>
                        <InputText
                            id="primaryActionPath"
                            value={form.primaryActionPath}
                            onChange={(e) => setField("primaryActionPath", e.target.value)}
                            placeholder="e.g. provider:/settings/public-profile"
                        />
                        <small className="text-color-secondary">
                            Use provider: or network: prefixes. Tokens: {"{providerSub}"}, {"{vanityName}"}.
                            Query params are supported (e.g. ?action=publish-profile).
                        </small>
                    </div>
                    <div className="field-checkbox">
                        <Checkbox
                            inputId="primaryActionAuthRequired"
                            checked={form.primaryActionAuthRequired === true}
                            onChange={(e) => setField("primaryActionAuthRequired", e.checked === true)}
                        />
                        <label htmlFor="primaryActionAuthRequired" className="ml-2">
                            Primary action requires authentication
                        </label>
                    </div>
                    <div className="field">
                        <label htmlFor="secondaryActionLabel">Secondary link label (optional)</label>
                        <InputText
                            id="secondaryActionLabel"
                            value={form.secondaryActionLabel}
                            onChange={(e) => setField("secondaryActionLabel", e.target.value)}
                            placeholder="e.g. View your public profile"
                        />
                    </div>
                    <div className="field">
                        <label htmlFor="secondaryActionPath">Secondary link path template (optional)</label>
                        <InputText
                            id="secondaryActionPath"
                            value={form.secondaryActionPath}
                            onChange={(e) => setField("secondaryActionPath", e.target.value)}
                            placeholder="e.g. network:/company/{vanityName}"
                        />
                    </div>
                    <div className="field-checkbox">
                        <Checkbox
                            inputId="secondaryActionAuthRequired"
                            checked={form.secondaryActionAuthRequired === true}
                            onChange={(e) => setField("secondaryActionAuthRequired", e.checked === true)}
                        />
                        <label htmlFor="secondaryActionAuthRequired" className="ml-2">
                            Secondary action requires authentication
                        </label>
                    </div>
                </>
            )}
        </Dialog>
    );
};

export default RecommendationCatalogDialog;
